/**
 * AcceptInvite Component
 *
 * Accept invitation page for users invited to join an organization.
 * Integrates with @cfleet/api-client hooks for backend API calls.
 *
 * Flow:
 * 1. If no token in URL, show token entry form
 * 2. Read token from URL params (?token=xxx) or manual entry
 * 3. Fetch invitation details via useInvitationByToken
 * 4. User sets password and submits
 * 5. Call useAcceptInvitation which returns LoginResponse
 * 6. Store tokens and navigate to dashboard (auto-login)
 */

import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useInvitationByToken, useAcceptInvitation } from '@cfleet/api-client';

import {
  ArrowRight,
  Lock,
  ShieldCheck,
  CheckCircle2,
  UserCheck,
  Mail,
  AlertCircle,
  Loader2,
  Link as LinkIcon,
} from 'lucide-react';

const AcceptInvite: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const token = searchParams.get('token');
  // Form state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Token entry form state (when no token in URL)
  const [manualTokenInput, setManualTokenInput] = useState('');
  const [tokenEntryError, setTokenEntryError] = useState<string | null>(null);

  // Fetch invitation details
  const {
    data: invitation,
    isLoading: invitationLoading,
    error: invitationError,
  } = useInvitationByToken(token);

  // Accept invitation mutation
  const {
    mutate: acceptInvite,
    isPending: acceptPending,
    error: acceptError,
  } = useAcceptInvitation();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    // Validate passwords
    if (password !== confirmPassword) {
      setValidationError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setValidationError('Password must be at least 8 characters');
      return;
    }

    if (!token) {
      setValidationError('Invalid invitation token');
      return;
    }

    acceptInvite(
      { token, password },
      {
        onSuccess: async (response) => {
          // Account created — redirect to sign-in (accept-invite no longer returns tokens)
          setSuccess(true);
          setTimeout(() => {
            navigate(`/signin?email=${encodeURIComponent(response.email)}`, { replace: true });
          }, 1500);
        },
        onError: (err) => {
          setValidationError(err instanceof Error ? err.message : 'Failed to accept invitation');
        },
      }
    );
  };

  const handleSignInClick = () => {
    navigate('/login');
  };

  /**
   * Extract token from input - handles both raw tokens and full URLs
   */
  const extractTokenFromInput = (input: string): string | null => {
    const trimmed = input.trim();
    if (!trimmed) return null;

    // Check if it's a URL containing a token
    try {
      const url = new URL(trimmed);
      const urlToken = url.searchParams.get('token');
      if (urlToken) return urlToken;
    } catch {
      // Not a valid URL, treat as raw token
    }

    // Return as raw token
    return trimmed;
  };

  const handleTokenSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTokenEntryError(null);

    const extractedToken = extractTokenFromInput(manualTokenInput);
    if (!extractedToken) {
      setTokenEntryError('Please enter your invitation code or paste the invitation link');
      return;
    }

    // Update URL params with the token - this will trigger re-render with token
    setSearchParams({ token: extractedToken });
  };

  // No token provided - show token entry form
  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 font-sans text-slate-900 relative overflow-hidden">
        {/* Background Decor */}
        <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-cfleet-100/50 to-transparent -z-10"></div>

        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden animate-in fade-in zoom-in-95 duration-500">
          <div className="p-8">
            {/* Logo */}
            <div className="flex justify-center mb-8">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 bg-cfleet-600 rounded-lg flex items-center justify-center text-white text-sm font-bold shadow-lg shadow-cfleet-500/30">
                  C
                </div>
                <span className="font-bold text-3xl text-slate-800 tracking-tight">cfleet</span>
              </div>
            </div>

            <div className="mb-8 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-cfleet-50 text-cfleet-600 rounded-full mb-4">
                <LinkIcon size={24} />
              </div>
              <h2 className="text-xl font-bold text-slate-900">Have an invitation?</h2>
              <p className="text-sm text-slate-500 mt-2">
                Enter the invitation code from your email or paste the full invitation link.
              </p>
            </div>

            {/* Error Message */}
            {tokenEntryError && (
              <div className="mb-6 p-3 bg-rose-50 border border-rose-200 rounded-lg flex items-start gap-2 animate-in fade-in slide-in-from-top-2">
                <AlertCircle size={18} className="text-rose-500 shrink-0 mt-0.5" />
                <p className="text-sm font-medium text-rose-700">{tokenEntryError}</p>
              </div>
            )}

            <form onSubmit={handleTokenSubmit} className="space-y-5">
              <div>
                <label
                  htmlFor="invitationCode"
                  className="block text-xs font-bold text-slate-700 uppercase mb-1.5 tracking-wide"
                >
                  Invitation Code or Link
                </label>
                <div className="relative group">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-cfleet-500 transition-colors">
                    <LinkIcon size={18} />
                  </div>
                  <input
                    id="invitationCode"
                    type="text"
                    value={manualTokenInput}
                    onChange={(e) => {
                      if (tokenEntryError) setTokenEntryError(null);
                      setManualTokenInput(e.target.value);
                    }}
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cfleet-500/20 focus:border-cfleet-500 transition-all placeholder:text-slate-400 font-medium"
                    placeholder="Paste your invitation code or link"
                  />
                </div>
                <p className="text-[10px] text-slate-400 mt-1.5 px-1">
                  Find this in your invitation email from cfleet.
                </p>
              </div>

              <button
                type="submit"
                className="w-full bg-cfleet-600 text-white py-3 rounded-lg text-sm font-bold shadow-lg shadow-cfleet-500/30 hover:bg-cfleet-700 hover:shadow-xl hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 mt-6"
              >
                Continue <ArrowRight size={18} />
              </button>
            </form>
          </div>

          <div className="px-8 py-5 bg-slate-50 border-t border-slate-100 text-center">
            <p className="text-xs text-slate-500 font-medium">
              Don't have an invitation?{' '}
              <button
                onClick={handleSignInClick}
                className="text-cfleet-600 font-bold hover:underline"
              >
                Sign in
              </button>{' '}
              or{' '}
              <button
                onClick={() => navigate('/signup')}
                className="text-cfleet-600 font-bold hover:underline"
              >
                create an account
              </button>
            </p>
          </div>
        </div>

        {/* Footer Info */}
        <div className="mt-8 text-center space-y-3">
          <div className="flex items-center justify-center gap-2 text-xs text-emerald-600 font-medium bg-emerald-50 px-3 py-1 rounded-full w-fit mx-auto border border-emerald-100">
            <ShieldCheck size={12} /> Secure Connection
          </div>
          <p className="text-xs text-slate-400">© 2025 cfleet. All rights reserved.</p>
        </div>
      </div>
    );
  }

  // Loading invitation details
  if (invitationLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 font-sans text-slate-900">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-100 p-8 text-center">
          <div
            role="status"
            className="w-12 h-12 border-4 border-slate-200 border-t-cfleet-600 rounded-full animate-spin mx-auto mb-4"
          >
            <span className="sr-only">Loading...</span>
          </div>
          <p className="text-slate-500">Loading invitation details...</p>
        </div>
      </div>
    );
  }

  // Invitation error (not found, expired, etc.)
  if (invitationError) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 font-sans text-slate-900">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-100 p-8 text-center">
          <div className="w-16 h-16 bg-rose-100 text-rose-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle size={32} />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Invitation Error</h2>
          <p className="text-slate-500 mb-6">
            {invitationError instanceof Error
              ? invitationError.message
              : 'Failed to load invitation'}
          </p>
          <button onClick={handleSignInClick} className="text-cfleet-600 font-bold hover:underline">
            Go to Sign In
          </button>
        </div>
      </div>
    );
  }

  // Get invitation data
  const invitedName = invitation?.name || 'Team Member';
  const companyName = invitation?.company_name || 'Your Company';
  const email = invitation?.email || '';

  // Combined error from validation or API
  const displayError =
    validationError || (acceptError instanceof Error ? acceptError.message : null);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 font-sans text-slate-900 relative overflow-hidden">
      {/* Background Decor */}
      <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-cfleet-100/50 to-transparent -z-10"></div>

      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden animate-in fade-in zoom-in-95 duration-500">
        {!success ? (
          <div className="p-8">
            {/* Logo */}
            <div className="flex justify-center mb-8">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 bg-cfleet-600 rounded-lg flex items-center justify-center text-white text-sm font-bold shadow-lg shadow-cfleet-500/30">
                  C
                </div>
                <span className="font-bold text-3xl text-slate-800 tracking-tight">cfleet</span>
              </div>
            </div>

            <div className="mb-8 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-cfleet-50 text-cfleet-600 rounded-full mb-4">
                <UserCheck size={24} />
              </div>
              <h2 className="text-xl font-bold text-slate-900">Finalize your account</h2>
              <p className="text-sm text-slate-500 mt-2">
                Hello <strong>{invitedName}</strong>, you're joining <strong>{companyName}</strong>.
              </p>
            </div>

            {/* Error Message */}
            {displayError && (
              <div className="mb-6 p-3 bg-rose-50 border border-rose-200 rounded-lg flex items-start gap-2 animate-in fade-in slide-in-from-top-2">
                <AlertCircle size={18} className="text-rose-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-rose-700">{displayError}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label
                  htmlFor="email"
                  className="block text-xs font-bold text-slate-700 uppercase mb-1.5 tracking-wide"
                >
                  Account Email
                </label>
                <div className="relative group">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                    <Mail size={18} />
                  </div>
                  <input
                    id="email"
                    type="email"
                    readOnly
                    value={email}
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-500 cursor-not-allowed font-medium"
                    title="This email is linked to your invitation and cannot be changed."
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <ShieldCheck size={16} className="text-emerald-500" />
                  </div>
                </div>
                <p className="text-[10px] text-slate-400 mt-1.5 px-1 italic">
                  Verified identity for this workspace.
                </p>
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-xs font-bold text-slate-700 uppercase mb-1.5 tracking-wide"
                >
                  Set Password
                </label>
                <div className="relative group">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-cfleet-500 transition-colors">
                    <Lock size={18} />
                  </div>
                  <input
                    id="password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => {
                      if (validationError) setValidationError(null);
                      setPassword(e.target.value);
                    }}
                    disabled={acceptPending}
                    className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cfleet-500/20 focus:border-cfleet-500 transition-all placeholder:text-slate-400 font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-xs font-bold text-slate-700 uppercase mb-1.5 tracking-wide"
                >
                  Confirm Password
                </label>
                <div className="relative group">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-cfleet-500 transition-colors">
                    <Lock size={18} />
                  </div>
                  <input
                    id="confirmPassword"
                    type="password"
                    required
                    value={confirmPassword}
                    onChange={(e) => {
                      if (validationError) setValidationError(null);
                      setConfirmPassword(e.target.value);
                    }}
                    disabled={acceptPending}
                    className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cfleet-500/20 focus:border-cfleet-500 transition-all placeholder:text-slate-400 font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={acceptPending}
                className="w-full bg-cfleet-600 text-white py-3 rounded-lg text-sm font-bold shadow-lg shadow-cfleet-500/30 hover:bg-cfleet-700 hover:shadow-xl hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 mt-6 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
              >
                {acceptPending ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : (
                  <>
                    Create Account & Join <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>
          </div>
        ) : (
          <div className="p-12 text-center animate-in zoom-in duration-300">
            <div className="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 size={40} />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Welcome Aboard!</h2>
            <p className="text-slate-500 mb-8">
              Your password has been set. Redirecting you to your workspace...
            </p>
            <div className="flex justify-center">
              <div className="w-12 h-1 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 animate-pulse origin-left"></div>
              </div>
            </div>
          </div>
        )}

        {!success && (
          <div className="px-8 py-5 bg-slate-50 border-t border-slate-100 text-center">
            <p className="text-xs text-slate-500 font-medium">
              Not your invitation?{' '}
              <button
                onClick={handleSignInClick}
                className="text-cfleet-600 font-bold hover:underline"
              >
                Sign in to another account
              </button>
            </p>
          </div>
        )}
      </div>

      {/* Footer Info */}
      <div className="mt-8 text-center space-y-3">
        <div className="flex items-center justify-center gap-2 text-xs text-emerald-600 font-medium bg-emerald-50 px-3 py-1 rounded-full w-fit mx-auto border border-emerald-100">
          <ShieldCheck size={12} /> Encrypted & Secure
        </div>
        <p className="text-xs text-slate-400">
          By joining, you agree to the company's workspace policies.
        </p>
      </div>
    </div>
  );
};

export default AcceptInvite;
